import { FormerCoachEntry } from '@cf-shared/data-model/coach';
import { computeAuthorIdentityHashAsync } from 'util/author-identity-hash';

export interface FormerCoachHashMatch {
  /** Hash → coachName, for tooltip resolution on matched UDEs. */
  hashToCoachName: Map<string, string>;
}

/**
 * Phase 3: resolve each former-coach `coachUid` to its `authorIdentityHash`
 * for picker / edit-exercise badge matching. The active coach is excluded
 * to avoid a sync-lag race where the stale `formerCoaches` entry coexists
 * with a re-populated `coachUid` for the same coach — without the exclusion
 * a UDE would be both hidden-as-active-coach AND tagged-as-former-coach.
 */
export async function resolveFormerCoachHashesAsync(
  formerCoaches: FormerCoachEntry[] | undefined,
  activeCoachUid: string | undefined,
): Promise<FormerCoachHashMatch> {
  const hashToCoachName = new Map<string, string>();
  if (!formerCoaches || formerCoaches.length === 0) return { hashToCoachName };
  const eligible = formerCoaches.filter(e => e.coachUid !== activeCoachUid);
  // Parallel hashing — formerCoaches is expected to be small but Promise.all
  // avoids serializing the SHA-256 calls. Insertion order is preserved by
  // operating on the filtered array's index alignment.
  const hashes = await Promise.all(eligible.map(e => computeAuthorIdentityHashAsync(e.coachUid)));
  for (let i = 0; i < eligible.length; i++) {
    const hash = hashes[i];
    // First entry wins on hash collision (oldest-first by insertion order).
    if (!hashToCoachName.has(hash)) {
      hashToCoachName.set(hash, eligible[i].coachName);
    }
  }
  return { hashToCoachName };
}
